import * as React from 'react';

// Modern, minimalist icons
const ICONS = {
  // Default marker with dot and pulse effect
  DEFAULT: `M12 0C7.58 0 4 3.58 4 8c0 5.76 7.44 15.36 7.75 15.75.19.23.47.25.67.05.31-.39 7.58-10 7.58-15.8C20 3.58 16.42 0 12 0zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z`,
  
  // Modern building icon
  BUILDING: `M17 11V3H7v4H3v14h18V11h-4zm-6-6h4v14h-4V5zm-6 4h4v10H5V9zm14 10h-4V13h4v6z`,
  
  // Modern company icon - office building
  COMPANY: `M12 7V3H2v18h20V7H12zm-5 12H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm0-4H5V5h2v2zm4 12H9v-2h2v2zm0-4H9v-2h2v2zm0-4H9V9h2v2zm0-4H9V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10z`,
  
  // Modern place/location icon
  PLACE: `M12 2C8.13 2 5 5.13 5 9c0 4.17 4.42 9.92 6.24 12.11.4.48 1.13.48 1.53 0C14.58 18.92 19 13.17 19 9c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z`,
  
  // Modern vehicle icon
  VEHICLE: `M18.92 5.01C18.72 4.42 18.16 4 17.5 4h-11c-.66 0-1.21.42-1.42 1.01L3 11v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 6h10.29l1.04 3H5.81l1.04-3zM19 16H5v-4.66l.12-.34h13.77l.11.34V16z`,
  
  // Modern tools icon
  TOOLS: `M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z`,
  
  // Modern tech icon
  TECH: `M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z`,
  
  // Modern furniture icon
  FURNITURE: `M20 8V6c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v2c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-6c0-1.1-.9-2-2-2zm-2-2v2H6V6h12zm2 10H4v-6h16v6z`
};

// Modern color scheme with better contrast and visibility
const CATEGORY_COLORS = {
  // Special entity types with more vibrant colors
  'COMPANY': '#FFC107', // Bright amber for better visibility
  'PLACE': '#4CAF50',   // Material green
  
  // Equipment categories with a modern palette
  'Heavy Equipment': '#FF5252',    // Red A200
  'Light Equipment': '#40C4FF',    // Light Blue A200
  'Medical Equipment': '#536DFE',  // Indigo A200
  'Office Equipment': '#69F0AE',   // Green A200
  'IT Equipment': '#448AFF',       // Blue A200
  'Laboratory Equipment': '#E040FB',// Purple A200
  'Safety Equipment': '#FF5252',   // Red A200
  
  // Vehicles with distinct shades
  'Commercial Vehicle': '#FF9800',
  'Construction Vehicle': '#F57C00',
  'Emergency Vehicle': '#D32F2F',
  'Fleet Vehicle': '#FF7043',
  
  // Tools with cool tones
  'Hand Tools': '#00BCD4',
  'Power Tools': '#00ACC1',
  'Precision Tools': '#0097A7',
  'Measuring Tools': '#00838F',
  
  // Machinery with industrial tones
  'Industrial Machinery': '#7C4DFF',
  'Manufacturing Machinery': '#651FFF',
  'Processing Machinery': '#6200EA',
  
  // Infrastructure with professional blues
  'Building': '#1976D2',
  'Facility': '#1565C0',
  'Infrastructure': '#0D47A1',
  
  // Technology with modern purples
  'Software': '#7B1FA2',
  'Hardware': '#6A1B9A',
  'Network Equipment': '#4A148C',
  
  // Others with neutral tones
  'Furniture': '#78909C',
  'Inventory': '#546E7A',
  'Supplies': '#455A64',
  'Other': '#2196F3'  // Material Blue
};

const getIconByCategory = (category: string) => {
  if (!category) return ICONS.DEFAULT;
  
  switch (true) {
    case category === 'COMPANY':
      return ICONS.COMPANY;
    case category === 'PLACE':
      return ICONS.PLACE;
    case /Vehicle/.test(category):
      return ICONS.VEHICLE;
    case /Building|Facility|Infrastructure/.test(category):
      return ICONS.BUILDING;
    case /Equipment|Tools|Machinery/.test(category):
      return ICONS.TOOLS;
    case /Software|Hardware|Network|IT/.test(category):
      return ICONS.TECH;
    case /Furniture/.test(category):
      return ICONS.FURNITURE;
    default:
      return ICONS.DEFAULT;
  }
};

interface PinProps {
  size?: number;
  category?: string;
}

function Pin({ size = 24, category }: PinProps) {
  const color = CATEGORY_COLORS[category || 'Other'] || '#2196F3';
  
  const pinStyle = {
    cursor: 'pointer',
    fill: color,
    filter: `drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2))`,
    transition: 'transform 0.2s ease-in-out, filter 0.2s ease-in-out',
    ':hover': {
      transform: 'scale(1.1)',
      filter: `drop-shadow(0 3px 3px rgba(0, 0, 0, 0.3))`
    }
  };

  return (
    <svg 
      height={size} 
      viewBox="0 0 24 24" 
      style={pinStyle}
      className="map-pin"
    >
      <path d={getIconByCategory(category)} />
    </svg>
  );
}

// Add styles for the pulse effect
const style = document.createElement('style');
style.textContent = `
  .map-pin {
    transform-origin: bottom center;
  }
  .map-pin:hover {
    transform: scale(1.1);
  }
`;
document.head.appendChild(style);

export default React.memo(Pin); 